<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <!-- Header -->
    <TopNavbar />

    <div class="flex w-full justify-between items-center px-4 py-4 bg-dark-grey">
      <div class="text-xl px-1 font-medium">
        <span class="text-total-white">Edit Contact</span>
      </div>

      <!-- Right Section: Save and Cancel Buttons -->
      <div class="flex items-center text-sm gap-2">
        <img
          v-if="isLoading"
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-8 h-8"
        />
      </div>
    </div>

    <!-- Edit Form -->
    <div
      :style="{ opacity: isLoading ? 0.5 : 1 }"
      class="flex-1 overflow-y-auto p-4 pb-[200px]"
    >
      <div class="max-w-4xl mx-auto">
        
        <!-- Profile Image (Optional) -->
        <div
          class="flex flex-col items-center gap-2 text-prime-golden underline underline-offset-4 justify-center mb-6"
        >
          <div
            class="w-20 h-20 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark relative"
          >
            <template v-if="contact.profile_image_url">
              <img
                :src="contact.profile_image_url"
                alt="Profile Image"
                class="w-full h-full object-cover rounded-full border z-2"
              />
              <div
                class="absolute top-0 right-0 bg-gray-500 w-6 h-6 rounded-full p-1 z-3 flex items-center justify-center border border-light-grey-translucent-dark"
                @click="removeImage"
              >
                <i class="fa fa-times text-white text-sm"></i>
              </div>
            </template>
            <template v-else>
              <!-- Placeholder icon -->
              <i class="fa-solid fa-user text-6xl text-pitch-black -mb-[1px] mt-auto"></i>
            </template>
          </div>
          <span class="relative overflow-hidden inline-block cursor-pointer">
            <!-- Visible Upload Text -->
            <span class="text-prime-golden rounded-full"> upload image </span>
            <!-- Hidden Input Field for Image Upload -->
            <input
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              type="file"
              accept="image/*"
              @change="handleImageUpload"
            />
          </span>
        </div>

        <form class="space-y-6 text-sm">
          
        <!-- Buttons Section: Save and Cancel -->
        <div class="flex items-center text-sm gap-2 mt-6 justify-end">
          <!-- Save Button -->
          <button
            @click="saveContact"
            class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
            :class="{ 'opacity-50': isLoading }"
            :disabled="isLoading"
          >
            Save
          </button>

          <!-- Cancel Button -->
          <button
            @click="goBack"
            class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
            :class="{ 'opacity-50': isLoading }"
            :disabled="isLoading"
          >
            Cancel
          </button>
        </div>
          <!-- First Name (Required) -->
          <div>
            <label for="first_name" class="text-left block mb-1">First Name *</label>
            <input
              id="first_name"
              v-model="contact.first_name"
              @input="errors.first_name = ''"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter first name"
            />
            <div v-if="errors.first_name" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.first_name }}
            </div>
          </div>

          <!-- Last Name (Optional) -->
          <div>
            <label for="last_name" class="text-left block mb-1">Last Name</label>
            <input
              id="last_name"
              v-model="contact.last_name"
              @input="errors.last_name = ''"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter last name"
            />
            <div v-if="errors.last_name" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.last_name }}
            </div>
          </div>

          <!-- Email (Optional) -->
          <div>
            <label for="email" class="text-left block mb-1">Email Address</label>
            <input
              id="email"
              v-model="contact.email"
              @input="errors.email = ''"
              type="email"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter email address"
            />
            <div v-if="errors.email" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.email }}
            </div>
          </div>

          <!-- Phone (Optional, hidden if original role is Project Manager) -->
          <div v-if="originalRole !== 'Project Manager'">
            <PhoneInput
              id="phone"
              v-model="contact.phone"
              @input="errors.phone = ''"
              label="Phone Number"
              placeholder="Enter phone number"
            />
            <div v-if="errors.phone" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.phone }}
            </div>
          </div>

          <!-- Company (Optional) -->
          <div>
            <label for="company" class="text-left block mb-1">Company</label>
            <input
              id="company"
              v-model="contact.company"
              @input="errors.company = ''"
              type="text"
              class="w-full p-3 bg-dark-grey rounded-md"
              placeholder="Enter company name"
            />
            <div v-if="errors.company" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.company }}
            </div>
          </div>

          <!-- Role (Required, hidden if original role is Project Manager) -->
          <div v-if="originalRole !== 'Project Manager'">
            <label for="role" class="text-left block mb-1">Role *</label>
            <CustomDropdown
              :options="roleOptions"
              v-model="contact.role"
              @input="errors.role = ''"
              :placeholder="'Enter role'"
              :allowOtherValues="true"
            />
            <div v-if="errors.role" class="text-deep-rust text-sm text-left mt-1">
              {{ errors.role }}
            </div>
          </div>

          <!-- General Error Message -->
          <div v-if="errorMessage" class="text-deep-rust text-center mt-2">
            {{ errorMessage }}
          </div>
        </form>

      </div>
    </div>

    <!-- Image Cropper Modal -->
    <div v-if="showCropper" class="cropper-modal">
      <cropper
        ref="cropperRef"
        class="max-h-[80dvh]"
        :src="imageToCrop"
        :aspect-ratio="1"
        :view-mode="1"
        @cropend="onCropEnd"
        :movable="false"
        :zoomable="false"
        :rotatable="false"
        :scalable="false"
      />
      <div class="flex gap-2 mt-4">
        <button
          @click="confirmCrop"
          class="bg-prime-golden text-pitch-black font-semibold py-1 px-3 rounded-lg"
        >
          Crop Image
        </button>
        <button
          @click="showCropper = false"
          class="bg-gray-500 text-total-white font-semibold py-1 px-3 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useContactsStore } from "@/stores/contact";
import TopNavbar from "@/components/TopNavbar.vue";
import CustomDropdown from "@/components/CustomDropdown.vue";
import { roleOptions } from "@/misc/util";
import PhoneInput from "@/components/PhoneInput.vue";
import Cropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import { useToast } from "vue-toastification";

const router = useRouter();
const route = useRoute();
const contactsStore = useContactsStore();

const isLoading = ref(false);
const originalRole = ref("");
const originalContact = ref(null);

const croppedImageFile = ref(null);

const contact = ref({
  id: null,
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  profile_image_url: "",
  projects: [],
});

const errors = ref({});
const errorMessage = ref("");

const showCropper = ref(false);
const imageToCrop = ref("");
const cropperRef = ref(null);

const toast = useToast();

const goBack = () => {
  if (route.query.projectId) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId },
      query: { tab: route.query.tab },
    });
  } else {
    router.go(-1);
  }
};

const isValidEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
  return re.test(email);
};

const validateForm = () => {
  errors.value = {};

  // Validate First Name (Required)
  if (!contact.value.first_name) {
    errors.value.first_name = "First name is required.";
  }

  // Last Name is optional – removed required validation

  // Validate Email (Optional)
  if (contact.value.email && !isValidEmail(contact.value.email)) {
    errors.value.email = "Please enter a valid email address.";
  }

  // Validate Phone (Optional)
  if (contact.value.phone && !/^\+?\d+$/.test(contact.value.phone.replace(/\s/g, ""))) {
    errors.value.phone =
      "Phone number must contain only digits, with an optional '+' at the start.";
  } else if (
    (contact.value.phone &&
      contact.value.phone.replace(/\s/g, "").replace(/^\+/, "").length < 7) ||
    contact.value.phone.replace(/\s/g, "").replace(/^\+/, "").length > 14
  ) {
    errors.value.phone = "Phone number must be between 7 to 14 digits.";
  }

  // Company is optional – removed required validation

  // Validate Role (Required)
  if (!contact.value.role) {
    errors.value.role = "Role is required.";
  } else if (contact.value.role == "Project Manager") {
    errors.value.role = "Editing the role to be Project Manager is not allowed.";
  }

  return Object.keys(errors.value).length === 0;
};
const saveContact = async () => {
  isLoading.value = true;

  if (!validateForm()) {
    isLoading.value = false;
    return;
  }

  try {
    // Prepare the update data - exclude profile_image_url initially
    const updateData = {
      first_name: contact.value.first_name,
      last_name: contact.value.last_name,
      email: contact.value.email,
      phone: contact.value.phone?.replace(/\s/g, ""),
      company: contact.value.company,
      role: contact.value.role,
      ...(route.query.projectId && { project_id: Number(route.query.projectId) }),
    };

    // Only include profile_image_url if there's a new cropped image or if image was explicitly removed
    if (croppedImageFile.value) {
      updateData.profile_image_url = "";
    } else if (contact.value.profile_image_url === "" && originalContact.value?.profile_image_url) {
      // Image was explicitly removed
      updateData.profile_image_url = "";
    }

    await contactsStore.updateContact(contact.value.id, updateData);

    if (croppedImageFile.value) {
      await contactsStore.uploadContactImage(contact.value.id, croppedImageFile.value);
    }
    if (route.query.projectId) {
      router.push({
        name: "ContactViewPage",
        params: { id: contact.value.id },
        query: { projectId: route.query.projectId },
      });
    } else {
      router.push({ name: "ContactViewPage", params: { id: contact.value.id } });
    }
  } catch (error) {
    console.error("Error saving contact:", error);
    toast.error("A problem occurred while updating the contact");

    errorMessage.value = "Failed to save contact.";
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  const contactId = route.params.id;

  if (contactId) {
    isLoading.value = true;
    if (contactsStore.contacts.length === 0) {
      try {
        await contactsStore.fetchAllContacts();
      } catch (error) {
        console.error("Error fetching contacts:", error);
        router.push({ name: "ContactBookPage" });
        return;
      }
    }

    const existingContact = contactsStore.contacts.find(
      (c) => c.id === Number(contactId)
    );

    if (existingContact) {
      contact.value = { ...existingContact };
      originalRole.value = contact.value.role;
      originalContact.value = { ...existingContact };
      console.log("Loaded contact:", contact.value);
      console.log("Profile image URL:", contact.value.profile_image_url);
    } else {
      console.error("Contact not found");
      router.push({ name: "ContactBookPage" });
    }
  } else {
    console.error("No contact ID provided");
    router.push({ name: "ContactBookPage" });
  }

  isLoading.value = false;
});

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      imageToCrop.value = e.target.result;
      showCropper.value = true;
    };
    reader.readAsDataURL(file);
  }
};

const confirmCrop = () => {
  const cropper = cropperRef.value;
  if (cropper) {
    const croppedCanvas = cropper.getCroppedCanvas();
    croppedCanvas.toBlob((blob) => {
      if (blob) {
        croppedImageFile.value = new File([blob], "cropped-image.png", {
          type: "image/png",
        });
      }
    });
    contact.value.profile_image_url = croppedCanvas.toDataURL();
    showCropper.value = false;
  }
};

const removeImage = () => {
  contact.value.profile_image_url = "";
  croppedImageFile.value = null;
};
</script>

<style scoped>
.cropper-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  height: 100dvh;
}
</style>
